import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoIcon from '@mui/icons-material/Info';
import { versionApi } from '../services/api';
import { format } from 'date-fns';

/**
 * Component to select and manage data versions
 * 
 * @param {Array} versions - List of available versions
 * @param {Object|null} selectedVersion - Currently selected version
 * @param {Function} onVersionChange - Callback when version changes
 * @param {Function} onRestoreVersion - Callback when version is restored
 * @param {Boolean} disabled - Whether the selector is disabled
 */
const VersionSelector = ({ 
  versions = [], 
  selectedVersion, 
  onVersionChange, 
  onRestoreVersion,
  disabled = false,
  campaignId
}) => {
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Fetch versions if not provided
  useEffect(() => {
    const fetchVersions = async () => {
      if (campaignId && (!versions || versions.length === 0)) {
        setLoading(true);
        try {
          const response = await versionApi.getVersions(campaignId);
          // Ensure we have an array of versions
          const versionsList = Array.isArray(response.data) ? response.data : [];
          
          // If there's a selected version, make sure it's in the list
          if (selectedVersion && !versionsList.some(v => v.id === selectedVersion.id)) {
            versionsList.unshift(selectedVersion);
          }
          
          // Update parent component with the first version if none selected
          if (!selectedVersion && versionsList.length > 0 && onVersionChange) {
            onVersionChange(versionsList[0]);
          }
        } catch (err) {
          console.error('Error fetching versions:', err);
          setError('Failed to load versions');
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchVersions();
  }, [campaignId, versions, selectedVersion, onVersionChange]);

  // Handle version change
  const handleVersionChange = (event) => {
    const versionId = event.target.value;
    
    if (!versionId) {
      if (onVersionChange) onVersionChange(null);
      return;
    }
    
    const version = versions.find(v => v.id.toString() === versionId.toString()) || null;
    if (onVersionChange) onVersionChange(version);
  };

  // Open restore version confirmation dialog
  const handleOpenRestoreDialog = () => {
    if (!selectedVersion) return;
    setRestoreDialogOpen(true);
  };

  // Close restore dialog
  const handleCloseRestoreDialog = () => {
    setRestoreDialogOpen(false);
  };

  // Restore the selected version
  const handleRestoreVersion = async () => {
    if (!selectedVersion) return;
    
    setRestoring(true);
    setError('');
    
    try {
      await versionApi.restoreVersion(selectedVersion.id);
      
      // Show success message
      setError('');
      
      // Call the onRestoreVersion callback if provided
      if (onRestoreVersion) {
        onRestoreVersion(selectedVersion);
      }
      
      // Close the dialog
      handleCloseRestoreDialog();
      
      // Refresh the page after a short delay to show the restored version
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error restoring version:', error);
      setError(error.response?.data?.detail || 'Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  // Format version label with date and time
  const formatVersionLabel = (version) => {
    if (!version) return '';
    
    try {
      const date = new Date(version.created_at);
      const formattedDate = format(date, 'dd/MM/yyyy HH:mm');
      return `${version.label || 'Untitled'} (${formattedDate})`;
    } catch (e) {
      return version.label || 'Untitled';
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" p={2}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography>Loading versions...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 300 }}>
        <Typography variant="subtitle1" sx={{ mr: 2, minWidth: 70 }}>
          Version:
        </Typography>
        
        <FormControl fullWidth size="small">
          <InputLabel id="version-select-label">Select Version</InputLabel>
          <Select
            labelId="version-select-label"
            id="version-select"
            value={selectedVersion?.id || ''}
            label="Select Version"
            onChange={handleVersionChange}
            disabled={disabled || versions.length === 0}
            sx={{ minWidth: 250 }}
          >
          <MenuItem value="">
            <em>Current (Latest)</em>
          </MenuItem>
          <MenuItem value="" disabled>
            <em>Select a version</em>
          </MenuItem>
          {versions.map((version) => (
            <MenuItem key={version.id} value={version.id}>
              {formatVersionLabel(version)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      </Box>

      <Box>
        <Tooltip title="Restore this version">
          <span>
            <IconButton
              color="primary"
              onClick={handleOpenRestoreDialog}
              disabled={!selectedVersion || disabled}
              size="large"
            >
              <RestoreIcon />
            </IconButton>
          </span>
        </Tooltip>
        
        {selectedVersion && selectedVersion.created_by && (
          <Tooltip 
            title={
              <>
                <div>Created by: {selectedVersion.created_by.username || 'Unknown'}</div>
                <div>Date: {new Date(selectedVersion.created_at).toLocaleString()}</div>
                {selectedVersion.description && (
                  <div>Description: {selectedVersion.description}</div>
                )}
              </>
            }
            arrow
          >
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      {error && (
        <Box width="100%" mt={1}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      
      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={restoring ? undefined : handleCloseRestoreDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Version Restore</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to restore version: 
            <strong>{selectedVersion ? formatVersionLabel(selectedVersion) : 'Unknown Version'}</strong>
            
            <Box component="div" sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.contrastText">
                <strong>Warning:</strong> This action will overwrite all current data with the selected version. 
                This action cannot be undone.
              </Typography>
            </Box>
            
            {selectedVersion?.description && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Version Notes:
                </Typography>
                <Typography variant="body2">
                  {selectedVersion.description}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseRestoreDialog} 
            disabled={restoring}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRestoreVersion} 
            color="primary" 
            variant="contained"
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
            sx={{ minWidth: 120 }}
          >
            {restoring ? 'Restoring...' : 'Restore Version'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VersionSelector; 